import type {LinkField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {AnimatePresence, motion} from 'framer-motion'
import {linkResolver} from 'prismicio'
import {useCallback, useEffect} from 'react'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {EXPENSE_POLICY_GENERATOR_ID} from 'slices/ExpensePolicyGenerator'
import {ROLES} from 'src/Event/components/RegisterForm/constants/formOptions'
import {useHubspotForm} from 'src/Event/hooks/useHubspotForm'
import {ButtonV2} from 'src/common/ButtonV2'
import {LeadFormFields} from 'src/common/LeadFormFields'
import {OPACITY_0, OPACITY_1} from 'src/common/constants'

import {DEFAULT_PDF_NAME} from '../constants'
import type {DownloadTemplateModalProps} from '../types'

export const DownloadTemplateModal = ({
  isOpen,
  onClose,
  previewImage,
  pdfLink,
  hubspotFormId,
  pageUid,
  trackingLocation,
  privacyNotice,
  copy,
}: DownloadTemplateModalProps) => {
  const hasGeneratorPrompt =
    isOpen && Boolean(copy.generatorLinkLabel) && Boolean(document.getElementById(EXPENSE_POLICY_GENERATOR_ID))

  const triggerDownload = useCallback(async () => {
    if (pdfLink.link_type !== 'Media' || !('url' in pdfLink)) {
      return
    }

    // Open PDF in a new tab
    window.open(pdfLink.url, '_blank', 'noopener,noreferrer')

    // Fetch as blob to force a real file download (download attribute is ignored for cross-origin URLs)
    try {
      const response = await fetch(pdfLink.url)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = ('name' in pdfLink && pdfLink.name) || DEFAULT_PDF_NAME
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch {
      // Fallback: at least the new tab is already open
    }
  }, [pdfLink])

  const {form, submit, isSubmitting} = useHubspotForm({
    formId: hubspotFormId,
    pageUid,
    trackingLocation,
    onSuccess: () => {
      triggerDownload()
      onClose()
    },
    trackDataType: 'download-template',
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      company: '',
      hs_persona: '',
      billing_country: 'United Kingdom',
    },
  })

  const {reset} = form

  // Reset form state when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset()
    }
  }, [isOpen, reset])

  // Lock body scroll while modal is open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.overflow = prev
      }
    }

    return undefined
  }, [isOpen])

  // Close modal on Escape key
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-moss-black/[70%] p-4 backdrop-blur-[10px]"
          initial={OPACITY_0}
          animate={OPACITY_1}
          exit={OPACITY_0}
          onClick={onClose}
        >
          <motion.div
            className="relative mx-auto grid w-full max-w-[820px] grid-cols-1 overflow-hidden rounded-2xl bg-moss-white shadow-2xl md:max-h-[90vh] md:grid-cols-2"
            initial={{opacity: 0, y: 24, scale: 0.97}}
            animate={{opacity: 1, y: 0, scale: 1}}
            exit={{opacity: 0, y: 12, scale: 0.97}}
            transition={{type: 'spring', damping: 28, stiffness: 300, mass: 0.8}}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute right-3 top-3 z-10 flex size-8 items-center justify-center rounded-full text-moss-grey-400 transition-colors hover:bg-moss-grey-100 hover:text-moss-black"
              aria-label={copy.closeLabel}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>

            <div className="flex flex-col justify-center gap-5 overflow-y-auto p-6 md:p-8">
              <div>
                <h2 className="text-h5 text-moss-black">{copy.heading}</h2>
                {copy.description && <p className="text-body-s mt-1 text-moss-grey-500">{copy.description}</p>}
              </div>

              <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(submit)} data-hs-do-not-collect>
                <LeadFormFields control={form.control} roleOptions={ROLES} />

                <ButtonV2
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                  className={cn('w-full', {'opacity-50': isSubmitting})}
                  trackingData={{
                    mktgId: 'download-template-form-submit',
                    location: trackingLocation,
                    dataType: 'download-template-submit',
                    pageName: pageUid,
                  }}
                >
                  {isSubmitting ? copy.submittingLabel : copy.submitLabel}
                </ButtonV2>

                {isFilled.richText(privacyNotice) && (
                  <PrismicRichText
                    field={privacyNotice}
                    components={{
                      paragraph: ({children}) => (
                        <p className="text-body-xs text-center text-moss-grey-400">{children}</p>
                      ),
                      hyperlink: ({node, children}) => {
                        const href =
                          node.data.link_type === 'Web' ? node.data.url : linkResolver(node.data as LinkField)

                        if (!href) {
                          return <>{children}</>
                        }

                        return (
                          <a
                            href={href}
                            target={node.data.link_type === 'Web' && node.data.target ? node.data.target : undefined}
                            rel="noopener noreferrer"
                            className="underline hover:text-moss-black"
                          >
                            {children}
                          </a>
                        )
                      },
                    }}
                  />
                )}

                {hasGeneratorPrompt && (
                  <p className="text-body-xs text-center text-moss-grey-500">
                    {copy.generatorPrompt && `${copy.generatorPrompt} `}
                    <button
                      type="button"
                      className="underline hover:text-moss-black"
                      onClick={() => {
                        onClose()

                        setTimeout(() => {
                          document.getElementById(EXPENSE_POLICY_GENERATOR_ID)?.scrollIntoView({behavior: 'smooth'})
                        }, 300)
                      }}
                    >
                      {copy.generatorLinkLabel}
                    </button>
                  </p>
                )}
              </form>
            </div>

            <div className="hidden overflow-hidden md:block">
              <PrismicNextImage field={previewImage} className="size-full object-cover object-top" alt="" />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
